import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ListRenderItem } from '@shopify/flash-list';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { List, ListRef } from 'components/List';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import { sortBy } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { eventTypeScrollToItem, NAVIGATE_MODAL } from 'store/actions';
import {
  selectRemoteComplianceSettingAssessmentTypes,
  selectRemoteComplianceSettingComplianceTypes,
} from 'store/remote/selectors';
import {
  SettingsAssessmentType,
  SettingsComplianceType,
} from 'types/ComplianceAndAssessmentTypeSettings';
import {
  getRowStatusFromCollapseAndShowState,
  RowStatus,
  calculateSearchState,
} from './Utils';
import { PageToolbar } from 'components/PageToolbar';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { randomKey } from 'lib/riskbase';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsComplianceAndAssessmentTypes
>;

type Row = {
  lastInGroup: boolean;
  childCount: number;
  collapsed: boolean;
  hiddenAssessmentTypesCount: number;
} & (
  | {
      type: 'complianceType';
      key: string;
      data: SettingsComplianceType;
    }
  | {
      type: 'assessmentType';
      key: string;
      data: SettingsAssessmentType;
    }
);

export const SettingsComplianceAndAssessmentTypesScreen: React.FC<Props> = ({
  navigation,
}) => {
  const screenSize = useScreenSize();
  const dispatch = useDispatch();

  const complianceTypes = useSelector(
    selectRemoteComplianceSettingComplianceTypes,
  )!;
  const assessmentTypes = useSelector(
    selectRemoteComplianceSettingAssessmentTypes,
  )!;

  const eventKey = React.useRef<string>(randomKey());

  // Collapse compliance type
  const [complianceCollapsed, setComplianceCollapsed] = React.useState<
    Record<string, boolean>
  >({});

  const searchTerm = React.useRef('');
  const [hasSearchTerm, setHasSearchTerm] = React.useState(
    !!searchTerm.current,
  );

  // Show all assessment types in a compliance type when searching
  const [complianceSearchShowAll, setComplianceSearchShowAll] = React.useState<
    Record<string, boolean>
  >({});

  // Compliance types matching search term
  const [matchingComplianceTypeIds, setMatchingComplianceTypeIds] =
    React.useState<string[]>([]);

  // Assessment types matching search term
  const [matchingAssessmentTypeIds, setMatchingAssessmentTypeIds] =
    React.useState<string[]>([]);

  const sortedComplianceTypes = React.useMemo(
    () => sortBy(Object.values(complianceTypes), 'name'),
    [complianceTypes],
  );

  const listRef = React.useRef<ListRef<Row>>(null);

  const sortedAssessmentTypesByComplianceTypeId = React.useMemo(
    () =>
      sortBy(Object.values(assessmentTypes), 'name').reduce<
        Record<string, (typeof assessmentTypes)[string][]>
      >((acc, aseType) => {
        if (!acc[aseType.complianceTypeId]) {
          acc[aseType.complianceTypeId] = [];
        }
        acc[aseType.complianceTypeId]!.push(aseType);
        return acc;
      }, {}),
    [assessmentTypes],
  );

  const handleSearchChange = React.useCallback(
    (value: string) => {
      const finalSearchTerm = value.trim();
      searchTerm.current = value;
      const {
        newCollapsedState,
        newShowAllState,
        newMatchingAssessmentTypeIds,
        newMatchingComplianceTypeIds,
      } = calculateSearchState({
        complianceTypes,
        sortedAssessmentTypesByComplianceTypeId,
        searchTerm: finalSearchTerm.toLowerCase(),
      });
      setHasSearchTerm(!!finalSearchTerm);
      setComplianceCollapsed(newCollapsedState);
      setComplianceSearchShowAll(newShowAllState);
      setMatchingAssessmentTypeIds(newMatchingAssessmentTypeIds);
      setMatchingComplianceTypeIds(newMatchingComplianceTypeIds);
    },
    [complianceTypes, sortedAssessmentTypesByComplianceTypeId],
  );

  const [headerHeight, setHeaderHeight] = React.useState<number>(0);
  const handleHeaderHeightChange = React.useCallback(
    (newHeaderHeight: number) => setHeaderHeight(newHeaderHeight),
    [],
  );

  const finalRows = React.useMemo(
    () =>
      sortedComplianceTypes.reduce<Row[]>((acc, complianceType) => {
        const complianceAssessmentTypes =
          sortedAssessmentTypesByComplianceTypeId[complianceType.id] ?? [];
        const hasMatchingComplianceType = matchingComplianceTypeIds.includes(
          complianceType.id,
        );

        // Skip compliance type if nothing matches search
        if (hasSearchTerm && !hasMatchingComplianceType) {
          return acc;
        }

        const matchingAssessmentTypes = complianceAssessmentTypes.filter(
          (aseType) => matchingAssessmentTypeIds.includes(aseType.id),
        );

        let rowStatus = getRowStatusFromCollapseAndShowState({
          hasSearchTerm,
          hasMatchingAssessmentTypes: !!matchingAssessmentTypes.length,
          isComplianceCollapsed: !!complianceCollapsed[complianceType.id],
          showAllAssessmentTypes: !!complianceSearchShowAll[complianceType.id],
        });
        const filteredAssessmentTypes =
          hasSearchTerm && rowStatus === RowStatus.matchingSearchTerm
            ? matchingAssessmentTypes
            : complianceAssessmentTypes;
        const lastIndex = filteredAssessmentTypes.length - 1;

        // Add compliance type then assessment types to list of items to render
        acc.push({
          key: `ct-${complianceType.id}`,
          type: 'complianceType',
          data: complianceType,
          lastInGroup: true,
          // childCount should show full count even if fewer matches returned
          childCount: complianceAssessmentTypes.length,
          collapsed: rowStatus === RowStatus.collapsed,
          hiddenAssessmentTypesCount: hasSearchTerm
            ? complianceAssessmentTypes.length - filteredAssessmentTypes.length
            : 0,
        });

        if (rowStatus !== RowStatus.collapsed) {
          acc.push(
            ...filteredAssessmentTypes.map<Row>((assessmentType, index) => ({
              key: `at-${assessmentType.id}`,
              type: 'assessmentType',
              data: assessmentType,
              lastInGroup: index === lastIndex,
              childCount: 0,
              collapsed: false,
              hiddenAssessmentTypesCount: 0,
            })),
          );
        }

        return acc;
      }, []),
    [
      matchingComplianceTypeIds,
      sortedComplianceTypes,
      sortedAssessmentTypesByComplianceTypeId,
      hasSearchTerm,
      complianceCollapsed,
      complianceSearchShowAll,
      matchingAssessmentTypeIds,
    ],
  );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventTypeScrollToItem, (event) => {
        if (event.payload.key === eventKey.current) {
          /*
           * Ordering occurs in this component. A row type is required.
           * This calls handleSearchChange to refresh the list, then gets the row to scroll to end.
           */

          handleSearchChange(searchTerm.current);

          const eventTypeOrSubtypeRow = finalRows.find((row) => {
            return row.key === event.payload.item;
          });

          if (!eventTypeOrSubtypeRow) {
            return;
          }

          listRef.current?.scrollToItem({
            item: eventTypeOrSubtypeRow,
            animated: event.payload.animated,
            viewOffset: headerHeight + theme.padding,
          });
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [
    listRef,
    headerHeight,
    eventKey,
    searchTerm,
    handleSearchChange,
    finalRows,
  ]);

  const handleToggleCollapse = React.useCallback(
    (sectionId: string) => {
      setComplianceCollapsed((prev) => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    },
    [setComplianceCollapsed],
  );

  const openNewComplianceTypeModal = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SettingsComplianceTypeModal,
        params: {
          eventKey: eventKey.current,
          complianceTypeId: null,
        },
      }),
    );

  const openComplianceType = React.useCallback(
    (resourceId: string | null) =>
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.SettingsComplianceTypeModal,
          params: {
            eventKey: eventKey.current,
            complianceTypeId: resourceId,
          },
        }),
      ),
    [dispatch],
  );

  const openAssessmentType = React.useCallback(
    (complianceTypeId: string, assessmentTypeId: string | null) =>
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.SettingsAssessmentTypeModal,
          params: {
            assessmentTypeId: assessmentTypeId,
            complianceTypeId: complianceTypeId,
            eventKey: eventKey.current,
          },
        }),
      ),
    [dispatch],
  );

  const renderItem: ListRenderItem<Row> = React.useCallback(
    ({ item }) => {
      if (item.type === 'complianceType') {
        const isCollapsed = item.collapsed;
        return (
          <Card
            borderRadiusBottom={isCollapsed || !item.childCount}
            style={
              isCollapsed || !item.childCount
                ? { marginBottom: theme.padding / 2 }
                : undefined
            }
          >
            <CardRow
              onPress={() => openComplianceType(item.data.id)}
              icon={ICONS.ChevronRightLight}
              onIconPress={() => handleToggleCollapse(item.data.id)}
              style={{ backgroundColor: '#ffffff80' }}
              iconStyle={{
                transform: [{ rotate: isCollapsed ? '0deg' : '90deg' }],
              }}
              indentActions
              menuOptions={[
                ...(item.hiddenAssessmentTypesCount > 0
                  ? [
                      {
                        text: 'Show All Assessment Types',
                        subText:
                          `${pluralise(item.hiddenAssessmentTypesCount, '1 type', '{x} types')} ` +
                          `hidden by search`,
                        onPress: () =>
                          setComplianceSearchShowAll((prev) => ({
                            ...prev,
                            [item.data.id]: true,
                          })),
                      },
                    ]
                  : []),
                {
                  text: 'Add Assessment Type',
                  onPress: () => openAssessmentType(item.data.id, null),
                },
              ]}
            >
              <Txt size="small" weight="bold">
                {item.data.name}
              </Txt>
              <Txt
                size="xxxsmall"
                style={{ color: theme.colors.fieldValueText }}
              >
                {pluralise(
                  item.childCount,
                  '1 Assessment Type',
                  '{x} Assessment Types',
                  'No Assessment Types',
                )}
              </Txt>
            </CardRow>
          </Card>
        );
      }

      if (item.type === 'assessmentType' && !item.collapsed) {
        return (
          <>
            <SeparatorHorizontal
              marginVertical={0}
              marginHorizontal={theme.padding}
            />
            <Card
              borderRadiusTop={false}
              borderRadiusBottom={item.lastInGroup}
              style={
                item.lastInGroup
                  ? {
                      marginBottom: theme.padding / 2,
                    }
                  : undefined
              }
            >
              <CardRow
                label={item.data.name}
                onPress={() =>
                  openAssessmentType(item.data.complianceTypeId, item.data.id)
                }
                labelAlignTop
              />
            </Card>
          </>
        );
      }

      return null;
    },
    [handleToggleCollapse, openComplianceType, openAssessmentType],
  );

  return (
    <Page
      header={
        <>
          <PageHeader
            title="Compliance and Assessment Types"
            onPressBackButton={
              screenSize === ScreenSize.small ? navigation.goBack : undefined
            }
            onPressSaveButton={openNewComplianceTypeModal}
            saveButtonIcon={ICONS.PlusLight}
          />
          <PageToolbar
            searchValue={searchTerm.current}
            onSearch={handleSearchChange}
            searchPlaceholder="Search Types"
            style={{ marginTop: theme.paddingVertical }}
          />
        </>
      }
      onHeaderChangeHeight={handleHeaderHeightChange}
    >
      {finalRows.length > 0 ? (
        <List
          listRef={listRef}
          data={finalRows}
          keyExtractor={(item) => item.key}
          getItemType={(item) => item.type}
          renderItem={renderItem}
          ItemSeparatorComponent={undefined}
        />
      ) : Object.values(assessmentTypes).length > 0 ? (
        <PageEmpty
          icon={ICONS.RBPlectrumLight}
          text="No Results Found"
          subtext={`No compliance or assessment\ntypes match your search`}
          button={{
            text: 'Compliance Type',
            icon: ICONS.PlusLight,
            onPress: openNewComplianceTypeModal,
          }}
        />
      ) : (
        <PageEmpty
          icon={ICONS.RBPlectrumLight}
          text="No Compliance or Assessment Types"
          button={{
            text: 'Compliance Type',
            icon: ICONS.PlusLight,
            onPress: openNewComplianceTypeModal,
          }}
        />
      )}
    </Page>
  );
};
